// src/components/Dashboard/CsrCollection.jsx
//
// Cobro de la comisión de CSR de UN cheque, dentro de la ficha del reclamo
// (tarjeta del cheque). Reemplaza a las pantallas sueltas ReceiveCheck y
// CheckPayment que vivían en "Nuevo reclamo". Tres partes, en orden:
//   1. Valores reales: monto, % del PA y % de CSR, con la calculadora.
//   2. Respaldo: backup del cheque (Hostinger).
//   3. Pago de la comisión: registrar pagos (con mensaje para el grupo) e
//      invoice. Con el pago completo el cheque queda "Comisión cobrada" solo
//      (por dentro se cierra el cobro y se genera la deuda del operador).
// Solo el equipo CSR lo edita; el PA lo ve.
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { archiveCheck, attachCheckPaymentBackup, getCheckById, markCheckPaid, updateCheckPayment, updateCheckRealValues, uploadCheckBackup } from '../../services/claimService';
import { openBackup, uploadBackup } from '../../services/backupService';
import { openCheckInvoice } from '../../utils/checkInvoice';
import { formatDateToMMDDYYYY } from '../../utils/dateHelpers';
import { isCheckReadyForPayment } from '../../utils/claimHelpers';
import { paymentReceivedDate, todayIso } from '../../utils/paymentCut';
import { buildPaidMessage, paidMessageFor } from '../../utils/paidMessage';
import './ClaimFlow.css';

const usd = value => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(value) || 0);
const valuesOf = check => ({
  checkDate: check.checkDate || '',
  payableTo: check.payableTo || '',
  amount: check.amount || '',
  adjusterPercentage: check.adjusterPercentage || '',
  csrPercentage: check.csrPercentage || 10,
  paPercentageConfirmed: Boolean(check.paPercentageConfirmed)
});

const CsrCollection = ({ check, isAdmin, language, onCheckChange, onPaid }) => {
  const es = language === 'es';
  const [editing, setEditing] = useState(!check.realValuesConfirmed);
  const [values, setValues] = useState(() => valuesOf(check));
  const [backupFile, setBackupFile] = useState(null);
  const emptyPayment = () => ({ amount: '', reference: '', receivedDate: todayIso(), file: null });
  const [payment, setPayment] = useState(emptyPayment);
  // Corrección de un pago ya registrado: { index, amount, date, reference, backupOnly }.
  const [payEdit, setPayEdit] = useState(null);
  // 'new' = pago nuevo (suma al cobrado y crea la deuda del operador) | 'backup' = solo respaldo de un pago ya registrado.
  const [payKind, setPayKind] = useState('new');
  const [busy, setBusy] = useState('');
  const canEdit = isAdmin && !check.paid;

  const refresh = async () => {
    const fresh = await getCheckById(check.id);
    if (fresh && onCheckChange) onCheckChange(fresh);
    return fresh;
  };
  const run = async (label, task, okMessage) => {
    setBusy(label);
    try {
      await task();
      if (okMessage) toast.success(okMessage);
    } catch (error) {
      console.error('CSR collection failed:', error);
      const messages = {
        CHECK_ALREADY_ARCHIVED: es ? 'La comisión de este cheque ya está cobrada.' : 'This commission is already collected.'
      };
      toast.error(messages[error.message] || (es ? `No se pudo completar: ${error.message}` : `Could not complete: ${error.message}`));
    } finally {
      setBusy('');
    }
  };

  const amount = Number(values.amount) || 0;
  const paPct = Number(values.adjusterPercentage) || 0;
  const csrPct = Number(values.csrPercentage) || 0;
  const paAmount = (amount * paPct) / 100;
  const csrAmount = (paAmount * csrPct) / 100;
  const hasBackup = Boolean(check.checkAttachment?.storageKey);
  const ready = isCheckReadyForPayment(check);
  const pending = check.pending ?? check.totalCollected ?? 0;

  const saveValues = () => {
    if (!(amount > 0) || !(paPct > 0)) { toast.error(es ? 'Completa el monto y el % del PA.' : 'Enter the amount and the PA %.'); return; }
    run('values', async () => { await updateCheckRealValues(check.id, values); await refresh(); setEditing(false); }, es ? 'Valores reales confirmados' : 'Real values confirmed');
  };
  const saveBackup = () => {
    if (!backupFile) { toast.error(es ? 'Elige el archivo del cheque.' : 'Choose the check file.'); return; }
    run('backup', async () => { await uploadCheckBackup(check.id, backupFile); setBackupFile(null); await refresh(); }, es ? 'Backup del cheque guardado' : 'Check backup saved');
  };

  const paidMessage = (total, reference, invoice = check.invoiceNumber) => buildPaidMessage(check, { total, reference, invoice, es });

  const saveBackupOnly = () => {
    if (!payment.file) { toast.error(es ? 'Elige el comprobante.' : 'Choose the receipt.'); return; }
    run('payment', async () => {
      await attachCheckPaymentBackup(check.id, payment.file, { reference: payment.reference, receivedDate: payment.receivedDate });
      setPayment(emptyPayment());
      await refresh();
    }, es ? 'Comprobante guardado como respaldo (no se registró un pago nuevo)' : 'Receipt saved as backup (no new payment recorded)');
  };

  const registerPayment = () => {
    if (payKind === 'backup' || check.paid) { saveBackupOnly(); return; }
    const amt = parseFloat(payment.amount);
    if (!amt || amt <= 0) { toast.error(es ? 'Ingresa un monto válido.' : 'Enter a valid amount.'); return; }
    if (!payment.receivedDate) { toast.error(es ? 'Indica la fecha en que se recibió el pago.' : 'Enter the date the payment was received.'); return; }
    run('payment', async () => {
      const backup = payment.file ? await uploadBackup({ file: payment.file, claimId: check.id, invoiceNumber: check.invoiceNumber, type: 'payment' }) : null;
      const result = await markCheckPaid(check.id, amt, { reference: payment.reference, receivedDate: payment.receivedDate, backup });
      // Si nunca se generó el invoice, el pago que completa el cheque le asigna número.
      const invoice = result.checkSnapshot?.invoiceNumber || check.invoiceNumber || '';
      let message;
      if (result.overpaidBy > 0) {
        message = es
          ? `PAGO CON EXCEDENTE\n\nSe recibió un pago de ${usd(amt)} para el invoice ${invoice}, que supera lo pactado (${usd(pending)}) por ${usd(result.overpaidBy)}.\n\nCliente: ${check.clientName}\nClaim: ${check.claimNumber}\n\nPróximo paso: confirmar con el P.A. cómo se maneja el excedente.`
          : `OVERPAYMENT RECEIVED\n\nA payment of ${usd(amt)} was received for invoice ${invoice}, exceeding the agreed amount (${usd(pending)}) by ${usd(result.overpaidBy)}.\n\nClient: ${check.clientName}\nClaim: ${check.claimNumber}\n\nNext step: confirm with the P.A. how to handle the surplus.`;
      } else if (result.isFullyPaid) {
        message = paidMessage(result.totalPaid, payment.reference, invoice);
      } else {
        message = es
          ? `PAGO PARCIAL REGISTRADO\n\n${invoice ? `Invoice ${invoice}.` : 'Invoice aún sin generar.'}\n\nCliente: ${check.clientName}\nMonto Recibido: ${usd(amt)}\nSaldo Pendiente: ${usd(result.remaining)}\n\nPróximo paso: pendiente ${usd(result.remaining)} para completar el invoice.`
          : `PARTIAL PAYMENT RECORDED\n\n${invoice ? `Invoice ${invoice}.` : 'Invoice not generated yet.'}\n\nClient: ${check.clientName}\nAmount Received: ${usd(amt)}\nRemaining Balance: ${usd(result.remaining)}\n\nNext step: pending ${usd(result.remaining)} to complete the invoice.`;
      }
      await navigator.clipboard.writeText(message).catch(() => {});
      setPayment(emptyPayment());
      // Pago completo: se cierra el cobro (genera la deuda del operador, como
      // antes al "archivar") y el cheque pasa a "Comisión cobrada".
      if (result.isFullyPaid) await archiveCheck(check.id);
      const fresh = await refresh();
      if (result.isFullyPaid && onPaid) await onPaid(fresh);
    }, es ? 'Pago registrado · mensaje copiado' : 'Payment recorded · message copied');
  };
  // El número nace aquí la primera vez: se puede enviar al PA antes del pago.
  const generateInvoice = () => run('invoice', async () => {
    await openCheckInvoice(check);
    if (!check.invoiceNumber) await refresh();
  });
  const copyPaidMessage = () => {
    navigator.clipboard.writeText(paidMessageFor(check, es))
      .then(() => toast.success(es ? 'Mensaje copiado' : 'Message copied'))
      .catch(() => toast.error(es ? 'No se pudo copiar' : 'Could not copy'));
  };

  const startEdit = (p, index) => setPayEdit({
    index, amount: String(p.amount ?? ''), date: paymentReceivedDate(p).date || todayIso(), reference: p.reference || '', backupOnly: Boolean(p.backupOnly)
  });
  const savePaymentEdit = () => {
    const amt = parseFloat(payEdit.amount);
    if (!payEdit.backupOnly && !(amt > 0)) { toast.error(es ? 'Ingresa un monto válido.' : 'Enter a valid amount.'); return; }
    if (!payEdit.date) { toast.error(es ? 'Elige la fecha de pago.' : 'Choose the payment date.'); return; }
    const errors = {
      PAYMENT_WOULD_UNPAY: es ? 'Este cheque ya está cobrado: el monto no puede quedar por debajo de lo pactado.' : 'This check is already collected: the amount cannot go below the agreed total.',
      BACKUP_ONLY_AMOUNT: es ? 'Este registro es solo un respaldo: su monto no se edita.' : 'This entry is only a backup: its amount cannot be edited.'
    };
    setBusy('edit');
    (async () => {
      try {
        const result = await updateCheckPayment(check.id, payEdit.index, {
          ...(payEdit.backupOnly ? {} : { amount: amt }), receivedDate: payEdit.date, reference: payEdit.reference
        });
        setPayEdit(null);
        if (result.becamePaid) {
          // La corrección saldó el cheque: igual que un pago completo.
          const invoice = result.checkSnapshot?.invoiceNumber || check.invoiceNumber || '';
          await navigator.clipboard.writeText(paidMessage(result.totalPaid, payEdit.reference, invoice)).catch(() => {});
          await archiveCheck(check.id);
          const fresh = await refresh();
          if (onPaid) await onPaid(fresh);
          toast.success(es ? 'Pago corregido · cheque saldado · mensaje copiado' : 'Payment fixed · check settled · message copied');
        } else {
          await refresh();
          toast.success(es ? `Pago corregido · saldo ${usd(result.remaining)}` : `Payment fixed · balance ${usd(result.remaining)}`);
        }
      } catch (error) {
        console.error('Payment edit failed:', error);
        toast.error(errors[error.message] || (es ? `No se pudo corregir: ${error.message}` : `Could not fix: ${error.message}`));
      } finally {
        setBusy('');
      }
    })();
  };
  const dateField = (
    <div className="cc-input-group"><label className="cc-label">{es ? 'Fecha de pago *' : 'Payment date *'}</label><input className="cc-input" type="date" max={todayIso()} value={payment.receivedDate} onChange={e => setPayment(p => ({ ...p, receivedDate: e.target.value }))} /></div>
  );

  const set = (key, value) => setValues(prev => ({ ...prev, [key]: value }));
  const done = (ok, label) => (
    <span className={`cd-status-badge ${ok ? 'cd-status-paid' : 'cd-status-pending'}`}>{ok ? '✓ ' : ''}{label}</span>
  );

  return (
    <div className="csr-collection">
      {/* 1. Valores reales */}
      <div className="csr-part">
        <div className="csr-part-head">
          <span className="csr-part-title">1. {es ? 'Valores reales del cheque' : 'Real check values'}</span>
          {done(check.realValuesConfirmed && !editing, check.realValuesConfirmed && !editing ? (es ? 'Confirmados' : 'Confirmed') : (es ? 'Por confirmar' : 'To confirm'))}
        </div>
        {editing && canEdit ? (
          <>
            <div className="cc-grid">
              <div className="cc-input-group"><label className="cc-label">{es ? 'Monto real *' : 'Real amount *'}</label><input className="cc-input" type="number" min="0" step="0.01" value={values.amount} onChange={e => set('amount', e.target.value)} /></div>
              <div className="cc-input-group"><label className="cc-label">{es ? '% del PA *' : 'PA % *'}</label><input className="cc-input" type="number" min="0" max="100" step="0.1" value={values.adjusterPercentage} onChange={e => set('adjusterPercentage', e.target.value)} /></div>
              <div className="cc-input-group"><label className="cc-label">{es ? '% de CSR (sobre lo del PA)' : 'CSR % (of the PA amount)'}</label><input className="cc-input" type="number" min="0" max="100" step="0.1" value={values.csrPercentage} onChange={e => set('csrPercentage', e.target.value)} /></div>
              <div className="cc-input-group"><label className="cc-label">{es ? 'Fecha del cheque' : 'Check date'}</label><input className="cc-input" type="date" value={values.checkDate} onChange={e => set('checkDate', e.target.value)} /></div>
              <div className="cc-input-group"><label className="cc-label">{es ? 'A nombre de (payable to)' : 'Payable to'}</label><input className="cc-input" value={values.payableTo} onChange={e => set('payableTo', e.target.value)} /></div>
            </div>
            <label className="check-toggle">
              <input type="checkbox" checked={values.paPercentageConfirmed} onChange={e => set('paPercentageConfirmed', e.target.checked)} />
              {es ? 'El PA confirmó los porcentajes' : 'The PA confirmed the percentages'}
            </label>
          </>
        ) : null}
        <div className="closing-totals">
          <div className="closing-total"><span>{es ? 'Cheque' : 'Check'}</span><strong>{usd(amount)}</strong></div>
          <div className="closing-total"><span>{`PA (${paPct}%)`}</span><strong>{usd(paAmount)}</strong></div>
          <div className="closing-total"><span>{es ? `CSR (${csrPct}% del PA)` : `CSR (${csrPct}% of PA)`}</span><strong>{usd(csrAmount)}</strong></div>
          <div className="closing-total"><span>{es ? 'Neto del PA' : 'PA net'}</span><strong>{usd(paAmount - csrAmount)}</strong></div>
        </div>
        {canEdit && (
          <div className="flow-actions">
            {editing
              ? <button type="button" className="cd-btn cd-btn-check" disabled={Boolean(busy)} onClick={saveValues}><i className="fa-solid fa-check"></i> {es ? 'Confirmar valores reales' : 'Confirm real values'}</button>
              : <button type="button" className="cd-btn" onClick={() => { setValues(valuesOf(check)); setEditing(true); }}><i className="fa-solid fa-pen"></i> {es ? 'Editar valores' : 'Edit values'}</button>}
          </div>
        )}
      </div>

      {/* 2. Respaldo: backup del cheque */}
      <div className="csr-part">
        <div className="csr-part-head">
          <span className="csr-part-title">2. {es ? 'Respaldo del cheque' : 'Check backup'}</span>
          {done(hasBackup, hasBackup ? (es ? 'Completo' : 'Complete') : (es ? 'Pendiente' : 'Pending'))}
        </div>
        <div className="check-docs">
          <div className={`check-doc ${hasBackup ? 'is-done' : ''}`}>
            <span className="check-doc-icon"><i className={`fa-solid ${hasBackup ? 'fa-circle-check' : 'fa-file-arrow-up'}`}></i></span>
            <span className="check-doc-name">{es ? 'Copia del cheque' : 'Check copy'}{hasBackup && check.checkAttachment?.name ? ` · ${check.checkAttachment.name}` : ''}</span>
            {hasBackup && <button type="button" className="cd-btn cd-btn-invoice" onClick={() => openBackup(check.checkAttachment.storageKey).catch(e => toast.error(e.message))}><i className="fa-solid fa-eye"></i> {es ? 'Ver' : 'View'}</button>}
            {canEdit && (
              <>
                <label className="cd-btn check-doc-upload">
                  <i className="fa-solid fa-paperclip"></i> {backupFile ? backupFile.name : (hasBackup ? (es ? 'Reemplazar' : 'Replace') : (es ? 'Elegir archivo' : 'Choose file'))}
                  <input type="file" accept="application/pdf,image/*" className="visually-hidden-input" aria-label={es ? 'Archivo del cheque' : 'Check file'} onChange={e => setBackupFile(e.target.files?.[0] || null)} />
                </label>
                {backupFile && <button type="button" className="cd-btn cd-btn-check" disabled={Boolean(busy)} onClick={saveBackup}><i className="fa-solid fa-upload"></i> {busy === 'backup' ? (es ? 'Subiendo…' : 'Uploading…') : (es ? 'Subir' : 'Upload')}</button>}
              </>
            )}
          </div>
        </div>
      </div>

      {/* 3. Pago de la comisión */}
      <div className={`csr-part ${ready || check.paid ? '' : 'is-locked'}`}>
        <div className="csr-part-head">
          <span className="csr-part-title">3. {es ? 'Pago de la comisión' : 'Commission payment'}</span>
          {done(check.paid, check.paid ? (es ? 'Comisión cobrada' : 'Commission collected') : `${es ? 'Saldo' : 'Balance'} ${usd(pending)}`)}
        </div>
        {!ready && !check.paid && <p className="flow-note">{es ? 'Se habilita cuando están confirmados los valores reales y el backup del cheque.' : 'Enabled once the real values and the check backup are confirmed.'}</p>}
        {(check.paymentHistory || []).length > 0 && (
          <ul className="csr-payments">
            {check.paymentHistory.map((p, index) => (
              <li key={index}>
                <strong>{usd(p.amount)}</strong>
                {(() => {
                  const received = paymentReceivedDate(p);
                  if (payEdit?.index === index) return (
                    <span className="csr-pay-edit">
                      <label className="csr-pay-edit-field"><span>{es ? 'Monto' : 'Amount'}</span>
                        <input className="cc-input" type="number" min="0" step="0.01" disabled={payEdit.backupOnly} value={payEdit.amount} onChange={e => setPayEdit(prev => ({ ...prev, amount: e.target.value }))} />
                      </label>
                      <label className="csr-pay-edit-field"><span>{es ? 'Fecha de pago' : 'Payment date'}</span>
                        <input className="cc-input" type="date" max={todayIso()} value={payEdit.date} onChange={e => setPayEdit(prev => ({ ...prev, date: e.target.value }))} />
                      </label>
                      <label className="csr-pay-edit-field"><span>{es ? 'Referencia' : 'Reference'}</span>
                        <input className="cc-input" value={payEdit.reference} onChange={e => setPayEdit(prev => ({ ...prev, reference: e.target.value }))} />
                      </label>
                      <span className="csr-pay-edit-actions">
                        <button type="button" className="cd-btn cd-btn-check" disabled={Boolean(busy)} onClick={savePaymentEdit}><i className="fa-solid fa-check"></i> {busy === 'edit' ? (es ? 'Guardando…' : 'Saving…') : (es ? 'Guardar' : 'Save')}</button>
                        <button type="button" className="cd-btn" disabled={Boolean(busy)} onClick={() => setPayEdit(null)}>{es ? 'Cancelar' : 'Cancel'}</button>
                      </span>
                      {payEdit.backupOnly && <small className="flow-muted">{es ? 'Registro de respaldo: solo se corrigen la fecha y la referencia.' : 'Backup entry: only the date and reference can be fixed.'}</small>}
                    </span>
                  );
                  return (
                    <span className="flow-muted">
                      {[
                        received.date ? `${es ? 'Fecha de pago' : 'Payment date'} ${formatDateToMMDDYYYY(received.date)}${received.estimated ? (es ? ' (fecha de registro, sin confirmar)' : ' (recording date, unconfirmed)') : ''}` : (es ? 'Sin fecha' : 'No date'),
                        p.reference
                      ].filter(Boolean).join(' · ')}
                      {isAdmin && (
                        <button type="button" className="cd-btn csr-pay-date-btn" disabled={Boolean(busy)} onClick={() => startEdit(p, index)} aria-label={es ? 'Editar pago' : 'Edit payment'} title={es ? 'Corregir monto, fecha o referencia' : 'Fix amount, date or reference'}><i className="fa-solid fa-pen" aria-hidden="true"></i> {es ? 'Editar' : 'Edit'}</button>
                      )}
                    </span>
                  );
                })()}
                {p.backup?.storageKey && <button type="button" className="cd-btn cd-btn-invoice" onClick={() => openBackup(p.backup.storageKey).catch(e => toast.error(e.message))}><i className="fa-solid fa-receipt"></i> {es ? 'Comprobante' : 'Receipt'}</button>}
              </li>
            ))}
          </ul>
        )}
        {isAdmin && (check.paid || (canEdit && ready)) && (
          <fieldset className="cc-input-group" disabled={Boolean(busy)}>
            <legend className="cc-label">{es ? 'Este comprobante es…' : 'This receipt is…'}</legend>
            {!check.paid && (
              <label className="cc-checkbox-row"><input type="radio" name={`paykind-${check.id}`} checked={payKind === 'new'} onChange={() => setPayKind('new')} /> {es ? 'Un pago nuevo (se suma a lo cobrado y crea la deuda del operador)' : 'A new payment (adds to collected and creates the operator debt)'}</label>
            )}
            <label className="cc-checkbox-row"><input type="radio" name={`paykind-${check.id}`} checked={check.paid || payKind === 'backup'} onChange={() => setPayKind('backup')} /> {es ? 'Solo un respaldo de un pago ya registrado (no cambia montos ni crea deuda)' : 'Only a backup of a payment already recorded (no amounts change, no debt)'}</label>
          </fieldset>
        )}
        {isAdmin && (check.paid || (canEdit && ready)) && (check.paid || payKind === 'backup') && (
          <div className="cc-grid">
            <div className="cc-input-group"><label className="cc-label">{es ? 'Referencia (ej. ZELLE-1234)' : 'Reference (e.g. ZELLE-1234)'}</label><input className="cc-input" value={payment.reference} onChange={e => setPayment(p => ({ ...p, reference: e.target.value }))} /></div>
            {dateField}
            <div className="cc-input-group"><label className="cc-label">{es ? 'Comprobante (BofA / Zelle) *' : 'Receipt (BofA / Zelle) *'}</label><input className="cc-input" type="file" accept="application/pdf,image/*" onChange={e => setPayment(p => ({ ...p, file: e.target.files?.[0] || null }))} /></div>
          </div>
        )}
        {canEdit && ready && !check.paid && payKind === 'new' && (
          <div className="cc-grid">
            <div className="cc-input-group"><label className="cc-label">{es ? 'Monto recibido *' : 'Amount received *'}</label><input className="cc-input" type="number" min="0" step="0.01" placeholder={usd(pending)} value={payment.amount} onChange={e => setPayment(p => ({ ...p, amount: e.target.value }))} /></div>
            <div className="cc-input-group"><label className="cc-label">{es ? 'Referencia (ej. ZELLE-1234)' : 'Reference (e.g. ZELLE-1234)'}</label><input className="cc-input" value={payment.reference} onChange={e => setPayment(p => ({ ...p, reference: e.target.value }))} /></div>
            {dateField}
            <div className="cc-input-group"><label className="cc-label">{es ? 'Captura del pago (opcional)' : 'Payment screenshot (optional)'}</label><input className="cc-input" type="file" accept="application/pdf,image/*" onChange={e => setPayment(p => ({ ...p, file: e.target.files?.[0] || null }))} /></div>
          </div>
        )}
        {isAdmin && (
          <div className="flow-actions">
            {canEdit && ready && !check.paid && <button type="button" className="cd-btn cd-btn-check" disabled={Boolean(busy)} onClick={registerPayment}><i className="fa-solid fa-dollar-sign"></i> {busy === 'payment' ? (es ? 'Guardando…' : 'Saving…') : payKind === 'backup' ? (es ? 'Guardar como respaldo' : 'Save as backup') : (es ? 'Registrar pago' : 'Record payment')}</button>}
            {check.paid && <button type="button" className="cd-btn cd-btn-check" disabled={Boolean(busy)} onClick={saveBackupOnly}><i className="fa-solid fa-receipt"></i> {busy === 'payment' ? (es ? 'Guardando…' : 'Saving…') : (es ? 'Guardar comprobante de respaldo' : 'Save backup receipt')}</button>}
            <button type="button" className="cd-btn cd-btn-invoice" disabled={Boolean(busy)} onClick={generateInvoice}><i className="fa-solid fa-file-invoice"></i> {es ? 'Generar invoice' : 'Generate invoice'}</button>
            {check.paid && <button type="button" className="cd-btn" onClick={copyPaidMessage}><i className="fa-solid fa-copy"></i> {es ? 'Copiar mensaje de pagado' : 'Copy paid message'}</button>}
          </div>
        )}
      </div>
    </div>
  );
};

export default CsrCollection;
